import { type Emitter } from "mitt";
import type { InjectionKey, Ref } from "vue";

import type { ChatAction } from "./chatActions";
import type { DictationChunk } from "./ChatDetail/types";
import type { MainMenuPane } from "./components/navigation/menuPanes";
import type { CHAT_EVENT } from "./events";
import type { Dimension, PerspectiveLensStrLiterals } from "./pages/perspectiveTypes";
import type { RealtimeClient } from "./StreamingPostClient";
import type { ActionState, ChatMessage, FocusWidgetParams, RolePlayAdjustment } from "./types/chat";

export interface StaffLink {
    url: string;
    title: string;
    icon: string;
    is_inertia?: boolean;
    method?: string;
    data?: Record<string, unknown>;
}

// A proxied dashboard the current member may open. Server-supplied because the client cannot
// know which dashboard an organization has, and it is served outside Inertia, so it is always a
// plain link rather than a router navigation.
export interface DashboardLink {
    url: string;
    title: string;
    icon: string;
}

// as more typing is added to pages that use mitt Events, add more typing here
export type MittEvents = {
    [CHAT_EVENT.DICTATION_EVENT]: { data: { message_id: string; dictation_count: number; audio_stream: string } };
    [CHAT_EVENT.SET_MESSAGE_TEXT]: string;
    [CHAT_EVENT.OPEN_TEXT_INPUT]: void;
    [CHAT_EVENT.OPEN_VOICE_INPUT]: void;
    [CHAT_EVENT.OPEN_MIC_TEST_DIALOG]: void;
    [CHAT_EVENT.FOCUS_CHAT_MESSAGE]: { chatMessageId: number };
    [CHAT_EVENT.FOCUS_WIDGET]: FocusWidgetParams;
    [CHAT_EVENT.FOCUS_ROLE_PLAY_MESSAGE_FEEDBACK]: number;
    [CHAT_EVENT.OPEN_THOUGHT_PROCESS_DIALOG]: number;
    [CHAT_EVENT.CHAT_MESSAGE]: { data?: ChatMessage };
    [CHAT_EVENT.WRAP_UP_CHAT]: void;
    [CHAT_EVENT.OPEN_SESSION_SUMMARY]: void;
    [CHAT_EVENT.WIDGET_ADDED]: ChatMessage;
    level: number;
    finish: DictationChunk;
    show_feedback_report: void;
    first_socket_message: Promise<void>;
    action_items: unknown; // stub
    end_role_play: void;
    customizable_text: { message_id: string | number; lineIdx: number; action_state: Partial<ActionState> };

    [ChatAction.ROLE_PLAY_ADJUST]: { message_id?: string | number; lineIdx?: number; action_state: Partial<ActionState>; action_params: { adjustment: RolePlayAdjustment } };

    [CHAT_EVENT.EDIT_MESSAGE]: { messageIdx: number; content: string; chatMessageUuid?: string };
    [CHAT_EVENT.STOP_GENERATING]: void;

    // Chat-level banners, rendered by ChatDetail. Payload is the message to display.
    error_notification: string;
    success_notification: string;
};

export type GlobalProperties = {
    $loadingImageSrc: string;
    $startChatUrl: string;
    $feedbackMailto: string;
    $setEventContext: (context: Record<string, string>) => void;
    $sendEvent: (eventName: string, data?: Record<string, unknown>) => Promise<unknown>;
    $sendSocketEvent: (event_type: string, data: Record<string, string>, socket: RealtimeClient) => void;
    isDebug: boolean;
    emitter: Emitter<MittEvents>;
};

export const panesWithNotificationKey = Symbol() as InjectionKey<Ref<MainMenuPane[]>>;
export const dismissNotificationLocallyKey = Symbol() as InjectionKey<(paneToDismiss: MainMenuPane) => void>;

export interface UserBasic {
    uuid?: string;
    full_name?: string;
    email?: string;
    job_title?: string;
    division?: string;
    is_manager?: boolean;
    organization?: string;
    has_perspective_report?: boolean;
}

/**
 * Shape of the "user_details" injectable (Inertia shared data).
 * When is_anonymous is true, only is_anonymous is guaranteed; other fields may be undefined.
 */
export interface UserDetails {
    is_anonymous: boolean;
    onboarding_checklist?: Record<string, unknown>;
    email?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    is_staff?: boolean;
    is_valencer?: boolean;
    is_demo?: boolean;
    valencer_instance_switcher?: {
        current: string | null;
        options: {
            key: string;
            label: string;
            host: string;
            url: string;
            is_current: boolean;
        }[];
    } | null;
    user_created_at?: string | null; // ISO datetime string
    prefers_default_to_voice?: boolean;
    prefers_weekly_check_in?: boolean;
    default_language?: string | null;
    sso_metadata?: Record<string, unknown> | null;
    is_manager?: boolean | null;
    has_calendar_integration?: boolean;
    timezone?: string | null;
    uuid?: string | null;
    hashid?: string | null;
    can_receive_emails?: boolean;
    can_see_client_analytics?: boolean;
    is_customer_demo_accounts_enabled?: boolean;
    calendar_first_onboarding_tools_enabled?: boolean;
    has_workiq_connected?: boolean;
    mic_consent_required?: boolean;
}

/**
 * Shape of the "organization_details" injectable (Inertia shared data).
 * Empty object when the user is not authenticated; null when authenticated but no organization.
 */
export interface OrganizationDetails {
    name?: string;
    logo_url?: string;
    logo_square_url?: string;
    is_default?: boolean;
    is_document_upload_enabled?: boolean;
    is_image_upload_enabled?: boolean;
    custom_privacy_modal_message?: string | null;
    is_chat_voice_input_hidden?: boolean;
    is_realtime_chat_enabled?: boolean;
    collapse_navbar_by_default?: boolean;
    custom_labels?: Record<string, string>;
    perf_review_custom_copy?: Record<string, string>;
    privacy_link_label?: string;
    privacy_modal_header?: string;
    team_tools_link_enabled?: boolean;
    show_outreach_consent?: boolean;
}

export interface UploadedFile {
    id: number;
    name: string;
    size: number;
    status: string;
    // Returned by /api/chat/document/upload but not the profile-upload endpoint;
    // optional so partial constructions in profile composables still type-check.
    description?: string;
    type?: string;
}

export interface PerspectiveReport {
    id: number;
    report_id: number;
    member_id: number;
    updated_at: string;
    member_email: string;
    member_first_name: string;
    member_last_name: string;
    member_entry_point: string;
    member_created_at: string;
    member_updated_at: string;
    mbti_letters: string | null;
    mbti_title: string | null;
    mbti_intro: string | null;
    mbti_population: string | null;
    type_cards: string[] | null;
    under_stress: {
        strategies?: Array<{ key: string; statement: string }>;
        reactions?: Array<{ key: string; statement: string }>;
    } | null;
    raw_perspective_data: Record<string, unknown>;
    dimensions?: PerspectiveReportDimension[];
}

export interface PerspectiveLensScore {
    lens: PerspectiveLensStrLiterals;
    raw_score: number;
    binned_score: number;
    binned_label: string;
}

export interface PerspectiveReportDimension {
    dimension: Dimension;
    title: string;
    subtitle: string | null;
    left_label: string;
    right_label: string;
    raw_score: number;
    binned_score: number;
    dimension_display_name: string;
    lenses: PerspectiveLensScore[];
}

export type HrisData = {
    first_name: string | null;
    last_name: string | null;
    job_title: string | null;
    department: string | null;
    location: string | null;
    hire_date: string | null;
    start_date: string | null;
    last_synced_at: string | null;
};

export type OrgTreePerson = {
    name: string;
    title: string;
    profile_uuid: string | null;
    has_profile: boolean;
    is_inactive?: boolean;
};

export type OrgTree = {
    manager: OrgTreePerson | null;
    direct_reports: OrgTreePerson[];
    peers: OrgTreePerson[];
};
